/**
 * Audio cues for hands-free session start.
 *
 * The scenario this exists for: you set the phone down, walk back to your desk,
 * and sit. You cannot see the screen, so the app has to tell you with sound
 * whether it is happy — and when it has actually started recording.
 *
 * iOS only lets audio start from a user gesture, and a suspended AudioContext
 * stays suspended forever if you miss that window. So `unlock()` is called from
 * the tap that arms the session, and every later cue reuses that context.
 *
 * Tones are synthesised: no asset to download, no licence, and the cues stay
 * distinguishable without being alarming — this runs next to someone working.
 */
export type Cue = 'armed' | 'ready' | 'start' | 'notReady' | 'finish' | 'paused' | 'restBreak';

interface ToneStep {
  /** Hz */
  freq: number;
  /** seconds */
  duration: number;
  /** seconds from the start of the cue */
  at: number;
  gain?: number;
  type?: OscillatorType;
}

/**
 * Cue vocabulary, chosen so they are told apart with your back to the phone:
 *  - armed     two soft mid tones: "I am watching, go and sit down"
 *  - ready     rising major third: conditions met, countdown begins
 *  - start     rising triad, louder: recording now
 *  - notReady  low double blip: still cannot measure you
 *  - finish    descending pair: session over
 *  - restBreak a gentle rising chime, played twice: time to get up for a bit
 */
const CUES: Record<Cue, ToneStep[]> = {
  armed: [
    { freq: 660, duration: 0.12, at: 0, gain: 0.18 },
    { freq: 660, duration: 0.12, at: 0.18, gain: 0.18 },
  ],
  ready: [
    { freq: 784, duration: 0.14, at: 0, gain: 0.22 },
    { freq: 988, duration: 0.18, at: 0.14, gain: 0.22 },
  ],
  start: [
    { freq: 784, duration: 0.12, at: 0, gain: 0.28 },
    { freq: 988, duration: 0.12, at: 0.12, gain: 0.28 },
    { freq: 1319, duration: 0.3, at: 0.24, gain: 0.3 },
  ],
  notReady: [
    { freq: 300, duration: 0.16, at: 0, gain: 0.2, type: 'triangle' },
    { freq: 240, duration: 0.22, at: 0.2, gain: 0.2, type: 'triangle' },
  ],
  finish: [
    { freq: 988, duration: 0.16, at: 0, gain: 0.24 },
    { freq: 660, duration: 0.3, at: 0.16, gain: 0.24 },
  ],
  paused: [{ freq: 440, duration: 0.2, at: 0, gain: 0.18, type: 'triangle' }],
  restBreak: [
    { freq: 1047, duration: 0.2, at: 0, gain: 0.24 },
    { freq: 1319, duration: 0.2, at: 0.2, gain: 0.24 },
    { freq: 1568, duration: 0.42, at: 0.4, gain: 0.26 },
    { freq: 1047, duration: 0.2, at: 1.2, gain: 0.24 },
    { freq: 1319, duration: 0.2, at: 1.4, gain: 0.24 },
    { freq: 1568, duration: 0.42, at: 1.6, gain: 0.26 },
  ],
};

type Ctx = AudioContext & { state: AudioContextState };

let context: Ctx | null = null;
let enabled = true;

function createContext(): Ctx | null {
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  try {
    return new Ctor() as Ctx;
  } catch {
    return null;
  }
}

/**
 * Must be called synchronously from a user gesture (the arm button tap).
 * Returns false when this browser will not give us audio at all, so the UI can
 * say so rather than silently failing on a hands-free start.
 */
export async function unlockCues(): Promise<boolean> {
  context ??= createContext();
  if (!context) return false;
  if (context.state === 'suspended') {
    try {
      await context.resume();
    } catch {
      return false;
    }
  }
  // A zero-gain blip: some versions of Safari only truly unlock after output.
  playSteps([{ freq: 440, duration: 0.01, at: 0, gain: 0.0001 }]);
  return context.state === 'running';
}

export function setCuesEnabled(value: boolean): void {
  enabled = value;
}

export function cuesAvailable(): boolean {
  return context?.state === 'running';
}

export function playCue(cue: Cue): void {
  if (!enabled) return;
  playSteps(CUES[cue]);
}

function playSteps(steps: ToneStep[]): void {
  if (!context || context.state !== 'running') return;
  const now = context.currentTime;
  for (const step of steps) {
    const oscillator = context.createOscillator();
    const gain = context.createGain();
    oscillator.type = step.type ?? 'sine';
    oscillator.frequency.value = step.freq;

    const start = now + step.at;
    const end = start + step.duration;
    const peak = step.gain ?? 0.2;
    // Short attack and release: a hard start/stop on a sine clicks audibly.
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, end);

    oscillator.connect(gain).connect(context.destination);
    oscillator.start(start);
    oscillator.stop(end + 0.02);
  }
}

/** Haptics where available; silent no-op elsewhere. Pairs with the audio cue. */
export function vibrate(pattern: number | number[]): void {
  if (!enabled) return;
  try {
    navigator.vibrate?.(pattern);
  } catch {
    // Unsupported; the sound cue is the primary channel anyway.
  }
}

export function releaseCues(): void {
  void context?.close().catch(() => {});
  context = null;
}
